import { toMinutes } from './schedule';

/**
 * The day plan's quiet helpers (1.24, Mike 18.10.): how much time is free between two
 * appointments, and which appointments happen at the same time for the same person. Pure, so the
 * Today page and the calendar's day view show exactly the same.
 */
export interface PlanSlot {
  id: string;
  start?: string;
  end?: string;
  /** Who is involved; 'family' = everybody. */
  people: string[] | 'family';
}

/** Without an end an appointment counts as one hour – as everywhere else in the app. */
const span = (s: PlanSlot) => {
  const a = toMinutes(s.start!);
  return [a, s.end ? Math.max(toMinutes(s.end), a + 1) : a + 60] as const;
};

/** Minutes free before an appointment (only gaps of at least `min`), keyed by its id. */
export function dayGaps(items: PlanSlot[], min = 30): Map<string, number> {
  const out = new Map<string, number>();
  let busyUntil: number | null = null;
  for (const s of [...items]
    .filter((i) => i.start)
    .sort((a, b) => a.start!.localeCompare(b.start!))) {
    const [a, b] = span(s);
    if (busyUntil !== null && a - busyUntil >= min) out.set(s.id, a - busyUntil);
    busyUntil = Math.max(busyUntil ?? 0, b);
  }
  return out;
}

const shares = (x: PlanSlot, y: PlanSlot) =>
  x.people === 'family' ||
  y.people === 'family' ||
  x.people.some((p) => (y.people as string[]).includes(p));

/**
 * For each appointment the first other one at the same time that involves the same person (or
 * the whole family). School next to a parent's work is no clash – they share nobody.
 */
export function overlaps(items: PlanSlot[]): Map<string, string> {
  const timed = items.filter((i) => i.start);
  const out = new Map<string, string>();
  for (const x of timed) {
    const [a, b] = span(x);
    const other = timed.find((y) => {
      if (y.id === x.id || !shares(x, y)) return false;
      const [c, d] = span(y);
      return a < d && c < b;
    });
    if (other) out.set(x.id, other.id);
  }
  return out;
}
